import {
  ArrowDownUp, CalendarDays, CheckSquare2, FileText, History,
  Mail, MessageCircle, Phone, Plus, RotateCcw,
  Search, StickyNote, Users,
  type LucideIcon,
} from 'lucide-react'
import type { ElevateItem } from '@solum/experience'
import { SearchField } from '../controls/SearchField'
import { Select } from '../controls/Select'
import s from './ElevateTimelineToolbar.module.css'

/**
 * The orders the DOOR offers, and therefore the only orders this control may.
 *
 * ★ `priority` is gone, and not as a simplification. activity.activity has
 * no priority column, so activity.contextual_timeline never returned one — the
 * option sorted every activity on `undefined`. It is the same defect as the
 * Unread/Pinned/With-attachments filters removed alongside it: a control over a
 * field nothing in the pipeline writes. The difference is that this one LOOKED
 * like it worked, because the other timeline sources do carry a priority, so it
 * reordered those few and left every activity where it was.
 *
 * The two that remain are allowlisted at the door as well, which refuses
 * anything else by name rather than coercing it to a default.
 */
export type ElevateTimelineSort = 'newest' | 'oldest'
export type ElevateTimelineGroup = 'context' | 'date' | 'type' | 'status' | 'owner' | 'none'

export interface ElevateTimelineView {
  query: string
  kinds: readonly string[]
  status: string
  sort: ElevateTimelineSort
  group: ElevateTimelineGroup
}

export const DEFAULT_ELEVATE_TIMELINE_VIEW: ElevateTimelineView = {
  query: '', kinds: [], status: '', sort: 'newest', group: 'context',
}

const KIND: Record<string, { label: string; icon: LucideIcon }> = {
  email: { label: 'Email', icon: Mail },
  note: { label: 'Note', icon: StickyNote },
  phone: { label: 'Phone call', icon: Phone },
  call: { label: 'Phone call', icon: Phone },
  task: { label: 'Task', icon: CheckSquare2 },
  appointment: { label: 'Appointment', icon: CalendarDays },
  meeting: { label: 'Meeting', icon: Users },
  teams: { label: 'Meeting', icon: Users },
  whatsapp: { label: 'Message', icon: MessageCircle },
  audit: { label: 'Change', icon: History },
  work: { label: 'Work item', icon: CheckSquare2 },
}

const kindOf = (item: ElevateItem) => (item.channel ?? item.kind).toLowerCase()

export function filterElevateTimeline(items: readonly ElevateItem[], view: ElevateTimelineView): ElevateItem[] {
  const query = view.query.trim().toLocaleLowerCase()
  const kinds = new Set(view.kinds)
  return items.filter((item) => {
    if (kinds.size > 0 && !kinds.has(kindOf(item))) return false
    if (view.status && item.status.id !== view.status) return false
    if (!query) return true
    /* The body is searched, and a REDACTED body is not: matching on
       `[redacted]` would let a search for that literal enumerate exactly which
       rows the boundary is hiding. */
    return [item.title, item.summary, item.body?.redacted ? undefined : item.body?.text,
      item.status.label, item.priority?.label,
      ...item.actors.map((actor) => actor.label), ...item.related.map((record) => record.label)]
      .filter(Boolean).some((value) => String(value).toLocaleLowerCase().includes(query))
  }).sort((a, b) => {
    /* ★ This orders the rows THIS FUNCTION was given, which for the paged
       activity source is one page. It is therefore a tie-keeper, not the sort:
       the real ordering happens at the door, ahead of the LIMIT, because a
       sort applied after a limit only reorders what the limit already chose.
       While it lived here alone, "Oldest" answered with the oldest of the
       NEWEST fifty and looked entirely confident about it.

       It stays because the feed is a UNION — audit, work and meeting rows do
       not come from that door and still have to be interleaved in the same
       direction as the page they are joining. */
    const byTime = a.time.effectiveAt.localeCompare(b.time.effectiveAt)
    return view.sort === 'oldest' ? byTime : -byTime
  })
}

/**
 * The choices this control may offer, counted, from a source that has NOT been
 * narrowed by the choice currently made in it.
 *
 * ★ The toolbar used to read its chips off the items it was holding. That was
 * harmless while filtering happened in the browser — the full set was always in
 * hand — and became a trap the moment the door began filtering: selecting an
 * email chip meant the door returned only emails, so email became the ONLY chip
 * and there was no way back except Reset. A control that erases its own options
 * as you use them is worse than one that offers too many.
 *
 * Supplying this is how a host says "I asked the governed question separately".
 * Omitting it falls back to the items, which is still right for any surface
 * that holds every row it could filter.
 */
export interface ElevateTimelineFacets {
  readonly kinds: readonly { readonly value: string; readonly n: number }[]
  readonly statuses: readonly { readonly value: string; readonly label?: string; readonly n: number }[]
}

export interface ElevateTimelineToolbarProps {
  items: readonly ElevateItem[]
  /** The governed vocabulary — see ElevateTimelineFacets. Omit only when the
   *  surface holds every row it could filter. */
  facets?: ElevateTimelineFacets
  value: ElevateTimelineView
  onChange: (next: ElevateTimelineView) => void
  onCreate?: () => void
  'data-testid'?: string
  onReset?: () => void
  sourceState?: 'ready' | 'partial' | 'error'
}

export function ElevateTimelineToolbar({ items, facets, value, onChange, onCreate, 'data-testid': createTestId, onReset, sourceState = 'ready' }: ElevateTimelineToolbarProps) {
  /* The governed vocabulary when the host supplied one, the page own only as a
     fallback. A kind currently SELECTED is always offered even if the scan did
     not reach it, because a chip you cannot unpress is the same trap in mirror
     image. */
  const availableKinds = [...new Set([
    ...(facets ? facets.kinds.map((entry) => entry.value) : []),
    /* Facets describe the activity door, while this is a MERGED feed: work,
       meetings and audit entries arrive from their own doors. They remain
       first-class filter choices even when activity supplies governed facets. */
    ...items.map(kindOf),
    ...value.kinds,
  ])].sort()
  const pageStatuses = [...new Map(items.map((item) => [item.status.id, item.status.label])).entries()]
  const statuses = facets
    ? [...new Map([
        ...facets.statuses.map((entry) => [entry.value, entry.label ?? entry.value] as [string, string]),
        // whatever the page can label better than the raw value
        ...pageStatuses,
        // A selected status survives a scan that did not reach it, so a
        // zero-result filter can still be cleared from the control itself.
        ...(value.status ? [[value.status, value.status] as [string, string]] : []),
      ]).entries()]
    : pageStatuses
  const active = value.query || value.kinds.length || value.status || value.sort !== 'newest'
    || value.group !== 'context'
  const change = (patch: Partial<ElevateTimelineView>) => onChange({ ...value, ...patch })
  const toggleKind = (kind: string) => change({
    kinds: value.kinds.includes(kind) ? value.kinds.filter((entry) => entry !== kind) : [...value.kinds, kind],
  })

  return (
    <section className={s.toolbar} aria-label="Timeline controls" data-testid="elevate-timeline-toolbar">
      <div className={s.headingRow}>
        <div>
          <strong>Timeline</strong>
          <span className={s.sourceState} data-state={sourceState}>{sourceState === 'ready' ? 'All sources available' : sourceState === 'partial' ? 'Some sources unavailable' : 'Timeline unavailable'}</span>
        </div>
        {onCreate && <button type="button" className={s.create} data-testid={createTestId} onClick={onCreate}><Plus aria-hidden="true" /> <span>Add</span></button>}
      </div>

      {availableKinds.length > 0 && (
        <div className={s.kindRow} role="toolbar" aria-label="Filter by activity type">
          {availableKinds.map((kind) => {
            const definition = KIND[kind] ?? { label: kind.replaceAll('_', ' '), icon: FileText }
            const Icon = definition.icon
            /* The COUNT comes from the same place the choice does. Counting
               the page would announce "Email, 0" for a kind the record has
               plenty of and the current filter simply excluded — a number
               that is worse than none, because it reads as a fact about the
               record rather than about the page. */
            const facetCount = facets?.kinds.find((entry) => entry.value === kind)?.n
            const count = facetCount ?? items.filter((item) => kindOf(item) === kind).length
            return <button key={kind} type="button" className={s.kindButton} data-kind={kind}
              aria-label={`${definition.label}, ${count}`} aria-pressed={value.kinds.includes(kind)} onClick={() => toggleKind(kind)}>
              <Icon aria-hidden="true" /><span className={s.srOnly}>{definition.label}</span>
            </button>
          })}
        </div>
      )}

      {/* #2502: the kit's value slots, not raw controls this sheet dressed. The
          search is `SearchField` (still a searchbox named "Search timeline",
          same placeholder); status, sort and group are the kit's `Select`,
          each named for what it chooses. "All status" is the Select's explicit
          EMPTY choice (`clearLabel`), because an empty value cannot be an
          ordinary option. */}
      <div className={s.findRow}>
        <SearchField wrapClassName={s.search} type="search" icon={<Search size={14} />} aria-label="Search timeline"
          value={value.query} onChange={(event) => change({ query: event.target.value })} placeholder="Search timeline…" />
        <span className={s.select}>
          <Select aria-label="Status" value={value.status} onValueChange={(status) => change({ status })}
            placeholder="All status" clearLabel="All status" testId="elevate-timeline-status"
            options={statuses.map(([id, label]) => ({ value: id, label }))} />
        </span>
      </div>

      <div className={s.organiseRow}>
        <span className={s.select}><ArrowDownUp aria-hidden="true" className={s.selectGlyph} />
          <Select aria-label="Sort" value={value.sort} testId="elevate-timeline-sort"
            onValueChange={(sort) => change({ sort: sort as ElevateTimelineSort })}
            options={[{ value: 'newest', label: 'Newest' }, { value: 'oldest', label: 'Oldest' }]} />
        </span>
        <span className={s.select}>
          <Select aria-label="Group" value={value.group} testId="elevate-timeline-group"
            onValueChange={(group) => change({ group: group as ElevateTimelineGroup })}
            options={[
              { value: 'context', label: 'Context' }, { value: 'date', label: 'Date' }, { value: 'type', label: 'Type' },
              { value: 'status', label: 'Status' }, { value: 'owner', label: 'Owner' }, { value: 'none', label: 'No groups' },
            ]} />
        </span>
        {/* Unread, Pinned and With-attachments used to sit here. Every one of
            them filtered on a field NOTHING produces: `unread`, `pinned` and
            `affordances.attachmentCount` have no writer anywhere in the
            pipeline -- the adapter never sets them and the door does not
            project them -- so each button could only ever empty the list.
            An offer the product cannot honour is worse than an absence,
            because the empty result reads as an answer about the data. They
            come back when a door supplies the facts, not before. */}
        {active && <button type="button" className={s.reset} onClick={() => { onReset?.(); onChange(DEFAULT_ELEVATE_TIMELINE_VIEW) }}><RotateCcw aria-hidden="true" /> Reset</button>}
      </div>
    </section>
  )
}
